/**
 * Communication step for the Schedule Interview wizard.
 * Dual-pane layout: Candidate (left) | Interviewer (right).
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Flex } from '@chakra-ui/react';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { useInterviewEmailTemplateOptions } from '../../../InterviewActivity/communication/api';
import { useInterviewActivityCommunicationVariables } from '../../../InterviewActivity/communication/api';
import {
  initialInterviewCommunicationFormState,
  interviewCommunicationReducer,
} from '../../../InterviewActivity/communication/state';
import {
  hydrateCommunicationStateFromPayload,
  toInterviewCommunicationPayload,
} from '../../../InterviewActivity/communication/helpers/payloadMap';
import {
  hasValidationErrors,
  validateInterviewCommunicationForm,
  type CommunicationValidationVariables,
  type InterviewCommunicationValidationErrors,
} from '../../../InterviewActivity/communication/validate';
import {
  getFirstScheduleCommunicationScrollTarget,
  scrollScheduleCommunicationSectionIntoView,
} from './scheduleCommunicationScroll';
import { calendarInviteTemplateNameForApi } from '../../../InterviewActivity/communication/helpers/calendarInviteName';
import { compositionFromTemplateOption } from '../../../InterviewActivity/communication/helpers/templateApply';
import { patchEmailComposition } from '../../../InterviewActivity/communication/helpers/emailUtils';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from '../../../InterviewActivity/communication/types';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useScheduleSenderOptions } from '../../hooks/useScheduleSenderOptions';
import type { ScheduleStepProps } from '../../config/schedule.types';
import { CandidatePane } from './CandidatePane';
import { InterviewerPane } from './InterviewerPane';

export const CommunicationStep = memo(
  forwardRef<StepFormApi, ScheduleStepProps>(
    (
      { payloadRef, mode, apiReference: _apiReference, prefillVersion = 0 },
      ref
    ) => {
      // ---- Reducer state ----
      const [state, dispatch] = useReducer(
        interviewCommunicationReducer,
        initialInterviewCommunicationFormState,
        (base) =>
          hydrateCommunicationStateFromPayload(
            Object.keys(payloadRef.current.communication).length > 0
              ? (payloadRef.current.communication as Partial<
                  Record<string, unknown>
                >)
              : undefined,
            base,
            { stripCalendarTemplates: prefillVersion > 0 }
          )
      );

      const validateCommOptions = useMemo(
        () => ({
          validateSelfSchedule:
            mode === 'self-schedule' || mode === 'self-schedule-adhoc',
        }),
        [mode]
      );

      useEffect(() => {
        if (validateCommOptions.validateSelfSchedule) {
          return;
        }
        dispatch({ type: 'SET_SELF_SCHEDULE_ENABLED', payload: false });
      }, [dispatch, validateCommOptions.validateSelfSchedule]);

      const [fieldErrors, setFieldErrors] =
        useState<InterviewCommunicationValidationErrors>({});

      // ---- Data fetching ----
      // Match InterviewActivity's per-section fetches so the calendar
      // dropdown only shows calendar-category templates and the email
      // dropdown only shows email-category templates (legacy wizard did
      // the same — a single merged scope left the wrong list in each
      // dropdown and picked the wrong default template).
      const { data: candidateCalendarTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate-calendar');
      const { data: candidateEmailTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate-email');
      const { data: interviewerCalendarTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer-calendar');
      const { data: interviewerEmailTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer-email');
      const { data: selfScheduleTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('self-schedule');
      const { fromOptions, ccBccOptions } = useScheduleSenderOptions();

      // The shared initial state seeds `from: 'organizer'` — that
      // sentinel is correct on InterviewActivity (stage-action plan),
      // but for a specific scheduled interview the From should default
      // to the user creating it. Swap each email's `from` once on mount
      // iff it still holds the organizer sentinel (the user hasn't
      // picked something else yet).
      const currUser = useSelector(selectCurrUser) as
        | { id?: number }
        | null
        | undefined;
      const didSeedFromRef = useRef(false);
      useEffect(() => {
        if (didSeedFromRef.current) return;
        const userId = currUser?.id;
        if (typeof userId !== 'number' || userId <= 0) return;
        // EmailCompositionValue.from is a string (option.value shape —
        // users come through as `String(u.id)`). Stringify before
        // writing so the Select value resolves to a real row.
        const fromValue = String(userId);
        didSeedFromRef.current = true;
        if (
          state.candidateEmail.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_CANDIDATE_EMAIL',
            payload: patchEmailComposition(state.candidateEmail, {
              from: fromValue,
            }),
          });
        }
        if (
          state.interviewerEmail.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_INTERVIEWER_EMAIL',
            payload: patchEmailComposition(state.interviewerEmail, {
              from: fromValue,
            }),
          });
        }
        if (
          state.selfSchedule.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_SELF_SCHEDULE',
            payload: patchEmailComposition(state.selfSchedule, {
              from: fromValue,
            }),
          });
        }
        // Intentionally only read state.* once — this is a one-shot seed
        // keyed on the ref, not a live sync.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currUser?.id]);

      const {
        candidateCalendar: candidateCalendarVariables,
        interviewerCalendar: interviewerCalendarVariables,
        candidateEmail: candidateEmailVariables,
        interviewerEmail: interviewerEmailVariables,
      } = useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();
      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      // ---- Validation variables ----
      const validationVariables: CommunicationValidationVariables = useMemo(
        () => ({
          candidateCalendar: candidateCalendarVariables,
          interviewerCalendar: interviewerCalendarVariables,
          candidateEmail: candidateEmailVariables,
          interviewerEmail: interviewerEmailVariables,
        }),
        [
          candidateCalendarVariables,
          candidateEmailVariables,
          interviewerCalendarVariables,
          interviewerEmailVariables,
        ]
      );

      // ---- Live validation after first submit failure ----
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!hasValidationErrors(prev)) return prev;
          return validateInterviewCommunicationForm(
            state,
            validationVariables,
            validateCommOptions
          );
        });
      }, [state, validationVariables, validateCommOptions]);

      // ---- One-shot template defaults (create mode only) ----
      // Prefill the first available template per section so the
      // subject/body/variables show up instead of a blank editor.
      // Mirrors useCommunicationStep.ts in InterviewActivity.
      const didDefaultCandidateCal = useRef(false);
      const didDefaultCandidateEmail = useRef(false);
      const didDefaultInterviewerCal = useRef(false);
      const didDefaultInterviewerEmail = useRef(false);
      const didDefaultSelfSchedule = useRef(false);

      useEffect(() => {
        if (prefillVersion > 0 || !candidateCalendarTemplateOptions.length) {
          return;
        }
        if (state.candidateCalendar.template) {
          didDefaultCandidateCal.current = true;
          return;
        }
        if (didDefaultCandidateCal.current) return;
        didDefaultCandidateCal.current = true;
        dispatch({
          type: 'SET_CANDIDATE_CALENDAR',
          payload: compositionFromTemplateOption(
            state.candidateCalendar,
            candidateCalendarTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        candidateCalendarTemplateOptions,
        state.candidateCalendar,
      ]);

      useEffect(() => {
        if (prefillVersion > 0 || !candidateEmailTemplateOptions.length) {
          return;
        }
        if (state.candidateEmail.template) {
          didDefaultCandidateEmail.current = true;
          return;
        }
        if (didDefaultCandidateEmail.current) return;
        didDefaultCandidateEmail.current = true;
        dispatch({
          type: 'SET_CANDIDATE_EMAIL',
          payload: compositionFromTemplateOption(
            state.candidateEmail,
            candidateEmailTemplateOptions[0]
          ),
        });
      }, [prefillVersion, candidateEmailTemplateOptions, state.candidateEmail]);

      useEffect(() => {
        if (prefillVersion > 0 || !interviewerCalendarTemplateOptions.length) {
          return;
        }
        if (state.interviewerCalendar.template) {
          didDefaultInterviewerCal.current = true;
          return;
        }
        if (didDefaultInterviewerCal.current) return;
        didDefaultInterviewerCal.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_CALENDAR',
          payload: compositionFromTemplateOption(
            state.interviewerCalendar,
            interviewerCalendarTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        interviewerCalendarTemplateOptions,
        state.interviewerCalendar,
      ]);

      useEffect(() => {
        if (prefillVersion > 0 || !interviewerEmailTemplateOptions.length) {
          return;
        }
        if (state.interviewerEmail.template) {
          didDefaultInterviewerEmail.current = true;
          return;
        }
        if (didDefaultInterviewerEmail.current) return;
        didDefaultInterviewerEmail.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_EMAIL',
          payload: compositionFromTemplateOption(
            state.interviewerEmail,
            interviewerEmailTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        interviewerEmailTemplateOptions,
        state.interviewerEmail,
      ]);

      useEffect(() => {
        if (prefillVersion > 0 || !selfScheduleTemplateOptions.length) return;
        if (!validateCommOptions.validateSelfSchedule) return;
        if (state.selfSchedule.template) {
          didDefaultSelfSchedule.current = true;
          return;
        }
        if (didDefaultSelfSchedule.current) return;
        didDefaultSelfSchedule.current = true;
        dispatch({
          type: 'SET_SELF_SCHEDULE',
          payload: compositionFromTemplateOption(
            state.selfSchedule,
            selfScheduleTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        selfScheduleTemplateOptions,
        state.selfSchedule,
        validateCommOptions.validateSelfSchedule,
      ]);

      // ---- Imperative handle ----
      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateInterviewCommunicationForm(
              state,
              validationVariables,
              validateCommOptions
            );
            setFieldErrors(errors);
            const ok = !hasValidationErrors(errors);
            if (!ok) {
              const target = getFirstScheduleCommunicationScrollTarget(errors);
              if (target) {
                scrollScheduleCommunicationSectionIntoView(target);
              }
            }
            return ok;
          },
          syncToPayload: () => {
            const comm = toInterviewCommunicationPayload(state);
            comm._ui_calendar_candidate_name = calendarInviteTemplateNameForApi(
              state.candidateCalendar,
              candidateCalendarTemplateOptions
            );
            comm._ui_calendar_interviewer_name =
              calendarInviteTemplateNameForApi(
                state.interviewerCalendar,
                interviewerCalendarTemplateOptions
              );
            payloadRef.current.communication = comm as unknown as Record<
              string,
              unknown
            >;
          },
        }),
        [
          candidateCalendarTemplateOptions,
          interviewerCalendarTemplateOptions,
          payloadRef,
          state,
          validationVariables,
          validateCommOptions,
        ]
      );

      // ---- Render ----
      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          gap="6"
          overflow="auto"
          pb="8"
        >
          <CandidatePane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            calendarTemplateOptions={candidateCalendarTemplateOptions}
            emailTemplateOptions={candidateEmailTemplateOptions}
            fromOptions={fromOptions}
            ccBccOptions={ccBccOptions}
            calendarVariables={candidateCalendarVariables}
            emailVariables={candidateEmailVariables}
            uploadInlineImage={uploadInlineImage}
          />
          <InterviewerPane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            calendarTemplateOptions={interviewerCalendarTemplateOptions}
            emailTemplateOptions={interviewerEmailTemplateOptions}
            fromOptions={fromOptions}
            ccBccOptions={ccBccOptions}
            calendarVariables={interviewerCalendarVariables}
            emailVariables={interviewerEmailVariables}
            uploadInlineImage={uploadInlineImage}
          />
        </Flex>
      );
    }
  )
);

CommunicationStep.displayName = 'CommunicationStep';
